import {
  GPS_FIX_MAX_PLAUSIBLE_GROUND_SPEED_KMH,
  GPS_FIX_MAX_UNEXPLAINED_DISPLACEMENT_M,
  GPS_FIX_RESYNC_S,
  GPS_SPIKE_MAX_FIXES,
  GPS_SPIKE_STEP_FACTOR,
  GPS_SPIKE_STEP_QUANTILE,
} from '../../analysis/config/telemetryChecks'
import { KMH_PER_MPS } from '../../utils/speedUnits'
import { MS_PER_S } from '../../utils/timeUnits'
import { maxOf } from '../../utils/extremes'
import { isUsableGpsFix, metresBetween, type GpsFixReading } from '../gpsFix'
import { runGpsStreamChecks } from '../streamChecks/runGpsStreamChecks'
import type { GpsStreamTrust } from '../streamChecks/gpsStreamTrust'

const hasSpeed = (
  fix: GpsFixReading
): fix is GpsFixReading & {
  speedKmh: number
} => fix.speedKmh !== undefined && Number.isFinite(fix.speedKmh)

const secondsBetween = (from: GpsFixReading, to: GpsFixReading): number =>
  (to.timeMs - from.timeMs) / MS_PER_S

const explainingSpeedKmh = (
  anchor: GpsFixReading,
  fix: GpsFixReading,
  speedIsTrusted: boolean
): number => {
  const reported = [anchor, fix].filter(hasSpeed).map((f) => f.speedKmh)
  return speedIsTrusted && reported.length > 0
    ? maxOf(reported)
    : GPS_FIX_MAX_PLAUSIBLE_GROUND_SPEED_KMH
}

const isStale = (anchor: GpsFixReading, fix: GpsFixReading): boolean =>
  secondsBetween(anchor, fix) > GPS_FIX_RESYNC_S

const isReachable = (
  anchor: GpsFixReading,
  fix: GpsFixReading,
  speedIsTrusted: boolean
): boolean => {
  const reachableMetres =
    (explainingSpeedKmh(anchor, fix, speedIsTrusted) / KMH_PER_MPS) *
      secondsBetween(anchor, fix) +
    GPS_FIX_MAX_UNEXPLAINED_DISPLACEMENT_M
  return metresBetween(anchor, fix) <= reachableMetres
}

const quantile = (values: number[], q: number): number => {
  const sorted = [...values].sort((a, b) => a - b)
  return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * q))] ?? 0
}

function keptInTime<T>(
  samples: T[],
  fixes: GpsFixReading[],
  speedIsTrusted: boolean
): T[] {
  const kept: T[] = []
  let anchor: GpsFixReading | null = null

  for (let i = 0; i < fixes.length; i++) {
    const fix = fixes[i]
    if (!isUsableGpsFix(fix)) continue

    if (anchor !== null && fix.timeMs <= anchor.timeMs) {
      kept.push(samples[i])
      continue
    }

    if (
      anchor === null ||
      isStale(anchor, fix) ||
      isReachable(anchor, fix, speedIsTrusted)
    ) {
      anchor = fix
      kept.push(samples[i])
    }
  }

  return kept
}

function keptInSteps<T>(samples: T[], fixes: GpsFixReading[]): T[] {
  const usable = fixes.filter(isUsableGpsFix)
  const movingSteps = usable
    .slice(1)
    .map((fix, i) => metresBetween(usable[i], fix))
    .filter((metres) => metres > 0)
  const reachPerStep =
    GPS_SPIKE_STEP_FACTOR * quantile(movingSteps, GPS_SPIKE_STEP_QUANTILE)
  const kept: T[] = []
  let anchor: GpsFixReading | null = null
  let stepsSinceAnchor = 0

  for (let i = 0; i < fixes.length; i++) {
    const fix = fixes[i]
    if (!isUsableGpsFix(fix)) continue
    stepsSinceAnchor++
    const reachable =
      anchor === null ||
      stepsSinceAnchor > GPS_SPIKE_MAX_FIXES ||
      metresBetween(anchor, fix) <=
        stepsSinceAnchor * reachPerStep + GPS_FIX_MAX_UNEXPLAINED_DISPLACEMENT_M
    if (reachable) {
      anchor = fix
      stepsSinceAnchor = 0
      kept.push(samples[i])
    }
  }

  return kept
}

export function rejectImplausibleGpsFixes<T>(
  samples: T[],
  read: (sample: T) => GpsFixReading,
  trust: GpsStreamTrust = runGpsStreamChecks(samples.map(read)).trust
): T[] {
  const fixes = samples.map(read)
  const kept = trust.timeTrusted
    ? keptInTime(samples, fixes, trust.speedTrusted)
    : keptInSteps(samples, fixes)
  return kept.length === samples.length ? samples : kept
}
